/**
 * Drag-to-select in the week grid (PLAN-085 §1.3). Pure maths, kept out of
 * `calendar-view.tsx` — that file is already 1 000 lines and the plan (§12)
 * asks for this to be extracted rather than inlined as more component state.
 *
 * The grid is a column per day, `hourHeight` pixels per hour, midnight at the
 * top. A drag is two y-offsets inside one day column.
 */

/** Selections snap to quarter hours. */
export const SNAP_MINUTES = 15
/** A click (or a very short drag) still means "book the smallest slot". */
export const MIN_SELECTION_MINUTES = 15

export interface GridSelection {
  dayIndex: number
  /** Minutes from midnight, snapped. */
  startMinutes: number
  endMinutes: number
}

const MINUTES_PER_DAY = 24 * 60

function snap(minutes: number): number {
  return Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES
}

/** Pixel offset inside a day column → snapped minutes from midnight. */
export function offsetToMinutes(offsetY: number, hourHeight: number): number {
  const raw = (offsetY / hourHeight) * 60
  return Math.min(MINUTES_PER_DAY, Math.max(0, snap(raw)))
}

/**
 * The selection a drag from `anchorY` to `currentY` describes. Dragging
 * upwards is the same selection as dragging downwards, and a selection is
 * never shorter than {@link MIN_SELECTION_MINUTES} — so a plain click books a
 * quarter of an hour instead of nothing.
 */
export function selectionFromDrag(
  dayIndex: number,
  anchorY: number,
  currentY: number,
  hourHeight: number
): GridSelection {
  const a = offsetToMinutes(anchorY, hourHeight)
  const b = offsetToMinutes(currentY, hourHeight)
  let startMinutes = Math.min(a, b)
  let endMinutes = Math.max(a, b)
  if (endMinutes - startMinutes < MIN_SELECTION_MINUTES) {
    endMinutes = startMinutes + MIN_SELECTION_MINUTES
  }
  if (endMinutes > MINUTES_PER_DAY) {
    endMinutes = MINUTES_PER_DAY
    startMinutes = Math.min(startMinutes, MINUTES_PER_DAY - MIN_SELECTION_MINUTES)
  }
  return { dayIndex, startMinutes, endMinutes }
}

/** Where to paint the selection overlay in the day column. */
export function selectionRect(
  selection: GridSelection,
  hourHeight: number
): { top: number; height: number } {
  const top = (selection.startMinutes / 60) * hourHeight
  const height = ((selection.endMinutes - selection.startMinutes) / 60) * hourHeight
  return { top, height }
}

/** The selection as absolute dates, given the day it was drawn on. */
export function selectionToDates(day: Date, selection: GridSelection): { start: Date; end: Date } {
  const start = new Date(day)
  start.setHours(0, 0, 0, 0)
  start.setMinutes(selection.startMinutes)
  const end = new Date(day)
  end.setHours(0, 0, 0, 0)
  end.setMinutes(selection.endMinutes)
  return { start, end }
}

/** `09:00 – 10:30`, for the selection overlay and the dialog header. */
export function formatSelectionRange(selection: GridSelection): string {
  return `${formatMinutes(selection.startMinutes)} – ${formatMinutes(selection.endMinutes)}`
}

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** `datetime-local` input value for a Date, in the browser's own timezone. */
export function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
